import { isEmpty, sortBy, difference } from 'lodash'
export const formatDate = (dateStringOrObject, defaultValue) => {
  if (typeof defaultValue === 'undefined') defaultValue = 'N/A'
  if (typeof dateStringOrObject === 'object') {
    dateStringOrObject = (dateStringOrObject || {}).effectiveDate
  }
  if (!dateStringOrObject) return defaultValue

  const date = new Date(dateStringOrObject)
  const year = date.getUTCFullYear()
  const month = (date.getUTCMonth() + 1) < 10 ? `0${date.getUTCMonth() + 1}` : date.getUTCMonth() + 1
  const day = date.getUTCDate() < 10 ? `0${date.getUTCDate()}` : date.getUTCDate()

  return `${month}/${day}/${year}`
}

export const formatDateForInput = (dateStringOrObject, defaultValue) => {
  const date = formatDate(dateStringOrObject, defaultValue)
  const [month, day, year] = date.split('/')
  return `${year}-${month}-${day}`
}

export const formatTime = (dateStringOrObject) => {
  if (typeof dateStringOrObject === 'object') {
    dateStringOrObject = (dateStringOrObject || {}).effectiveDate
  }
  if (!dateStringOrObject) { return 'N/A' }
  const date = new Date(dateStringOrObject)
  const time = date.toLocaleTimeString()

  return `${time}`
}

export const formatPercent = (rate, numberOfDecimals) => `${(rate * 100).toFixed(numberOfDecimals || 2)}%`
export const formatPercentChanged = rate => {
  const pct = Number((Math.abs((rate - 1)) * 100).toFixed(2))
  if (!pct || pct === 0) return ''
  return `${rate >= 1 ? '+' : '-'}${pct}%`
}

export const toTitleCase = (str, delimiters) => {
  if (!str) return str
  if (typeof delimiters === 'undefined' || !delimiters.length) delimiters = [' ']

  str = str.toLowerCase()
  for (const d of delimiters) {
    str = str.split(d)
    .map(word => word ? word.replace(word[0], word[0].toUpperCase()) : word)
    .join(d)
  }
  return str
}

export const filterObjects = (array, filterString) => {
  if (!array.length || !filterString) return array
  filterString = filterString.toUpperCase()

  const keys = Object.keys(array[0])
  return array.filter(obj =>
    keys.some(key => {
      if (obj[key]) {
        if (typeof obj[key] === 'string') {
          return obj[key].toUpperCase().includes(filterString)
        } else if (Array.isArray(obj[key])) {
          return obj[key].join('; ').toUpperCase().includes(filterString)
        }
      }
      return false
    })
  )
}

export const isoDateString = (dateString, includeTime) => {
  if (!dateString) return dateString
  if (includeTime && dateString.length === 10) return dateString + 'T00:00:00'
  if (!includeTime) return dateString.replace(/T[0:]+$/g, '')
  return dateString
}

export const serializeError = err => JSON.stringify(err, ['name', 'message', 'stack', 'arguments', 'type'], 2)

/* Utility Fns for Filtering down Product Subscriptions (used by SubscriptionComponent and AddUsersComponent) */
export const checkProductActive = regions => !isEmpty(regions.filter(x => (x.accessStatus || '').toLowerCase() === 'access granted' || ((x.accessStatus || '').toLowerCase() === 'access pending' && (x.operationType || '').toLowerCase() === 'add')))
export const checkProductPending = regions => !isEmpty(regions.filter(x => (x.accessStatus || '').toLowerCase() === 'access pending'))
export const checkProductPurchased = regions => !isEmpty(regions.filter(x => x.purchased))
export const determineProductGroups = productGroups => {
  return productGroups.filter(productGroup => !isEmpty(productGroup.products.find(product => !isEmpty(product.regions.find(region => region.purchased)))))
}
export const determineProducts = products => {
  const hasCodeSourceDataFiles = !isEmpty(products.find(product => product.name === 'CODESOURxCE - Web & Data Files' && !isEmpty(product.regions.find(region => region.purchased))))
  if (hasCodeSourceDataFiles) return products.filter(product => product.name === 'CODESOURxCE - Web & Data Files')
  return products
}
export const determineRegions = regions => {
  const hasAllInternational = !isEmpty(regions.find(region => region.name === 'All International' && region.purchased))
  if (hasAllInternational) return regions.filter(region => region.name === 'US' || region.name === 'All International')
  return regions
}

export const requesterEmailAddress = props =>
  props.history &&
      props.history.location &&
      props.history.location.search
  ? queryString(props.history.location.search)['email'] || props.emailAddress
  : props.emailAddress

export const queryString = search => {
  const qs = {}
  if (search && search[0] === '?') search = search.substr(1)
  const params = (search || '').split('&')
  for (const param of params) {
    const p = param.split('=', 2)
    const key = decodeURIComponent(p[0])
    const value = decodeURIComponent(p[1])
    qs[key] = value
  }
  return qs
}

export const sortRegions = regions => {
  const active = regions.filter(x => x.accessStatus === 'access granted')
  const pendingActive = regions.filter(x => x.accessStatus === 'access pending' && x.operationType === 'Add')
  const others = difference(regions, [...active, ...pendingActive])
  const order = ['USA', 'ALL International', 'Europe', 'Canada', 'Japan', 'Russia', 'China', 'Brazil']
  const orderMap = {}
  order.forEach(x => { orderMap[x] = order.indexOf(x) })
  const sortedActive = sortBy(active, [region => orderMap[region.name]])
  const sortedPendingActive = sortBy(pendingActive, [region => orderMap[region.name]])
  const sortedOthers = sortBy(others, [region => orderMap[region.name]])
  return [...sortedActive, ...sortedPendingActive, ...sortedOthers]
}

export const getReturnUrlDomain = () => {
  let domain = 'https://secure.ipdanalytics.com'
  try {
    const m = (document.referrer || '').match(/(https?:\/\/.[^/]+)/)
    if (m && ['https://secure.ipdanalytics.com', 'https://beta-secure.ipdanalytics.com', 'http://localhost:8085'].indexOf(m[1]) > -1) {
      domain = m[1]
    }
  } catch (error) {
    console.error(error)
  }

  return domain
}

export const getReturnUrlPath = () => {
  let returnUrl = getReturnUrlDomain().match(/localhost/) ? '/Home' : '/User/Home'
  try {
    returnUrl = queryString(document.location.search)['returnUrl'] || returnUrl
  } catch (error) {
    console.error(error)
  }

  return returnUrl
}

export const isCurrentDateWithinSuspendDates = (begin, end) => {
  const currentDate = formatDateForInput(new Date().toISOString())
  return !isEmpty(begin) && !isEmpty(end)
    ? currentDate >= begin && currentDate <= end
    : !isEmpty(begin) && isEmpty(end)
      ? currentDate >= begin
      : false
}

export const lookupLabel = (key) => {
  if (!key) return ''

  switch (key) {
    case 'firstName':
      return 'First Name'
    case 'lastName':
      return 'Last Name'
    case 'country':
      return 'Country'
    case 'department':
      return 'Department'
    case 'emailAddress':
      return 'Email Address'
    case 'jobTitle':
      return 'Job Title'
    case 'officePhone':
      return 'Office Phone'
    default:
      return ''
  }
}
